// [PHASE B / #022 — string-sin batch 2 site 4 partial]: Walk a character's
//   classes and answer "does any of them own this combat-toggle flag?"
//   by reading the `combatToggles` array on the resolved class data
//   (SRD_CLASSES + custom sources via contentResolver.getClassData).
//
// Pre-fix combatToggles.js's `isRelevant` predicates for class-only
// toggles were:
//
//     isRelevant: (ps) => ps.classes.some(c => c.className === 'Rogue')
//
// — hardcoded D&D 5e class names. The new schema makes ownership a
// property of the class itself:
//
//     SRD_CLASSES.Rogue.combatToggles = [{ flag: 'sneakAttackActive' }];
//     SRD_CLASSES.Barbarian.combatToggles = [{ flag: 'recklessAttackActive', minLevel: 2 }];
//     SRD_CLASSES.Ranger.combatToggles = [
//       { flag: 'huntersMarkTargetActive' },
//       { flag: 'attackingFavoredEnemy' },
//     ];
//
// Custom CW classes can declare their own toggle ownership the same way.
// The closed-set toggle DEFINITIONS (the icon/description/flag triple)
// stay in combatToggles.js; only OWNERSHIP moves to the class data block.
//
// Per Rule 22, the helper is the single source of truth — both
// combatToggles.js's predicates and the unit test import from here.
//
// Out of scope for this commit: subclass-owned toggles, state-gated
// (Hunter Colossus Slayer subclassData.hunterOptions read), multi-
// subclass ownership lists (Cleric Knowledge/Light/Grave/Peace at L8+).
// Those remain in custom predicates pending a richer schema design in
// the Bucket 1 architectural pass.

import { getClassData, getSubclassData } from './contentResolver';

/**
 * Match a flag against a `combatToggles` array, honoring optional
 * `minLevel` gating per entry. Entries can be either bare flag strings
 * or objects of shape `{ flag, minLevel? }`.
 */
function matchesToggleEntry(toggles, flag, classLevel) {
  if (!Array.isArray(toggles)) return false;
  return toggles.some((t) => {
    const tFlag = typeof t === 'string' ? t : t?.flag;
    if (tFlag !== flag) return false;
    const minLevel = (typeof t === 'object' && t?.minLevel) || 0;
    return classLevel >= minLevel;
  });
}

/**
 * Does any class (or its chosen subclass) on `playerStats.classes`
 * declare ownership of the given combat-toggle flag at its current level?
 *
 * Walks each playerStats.classes[i] entry and resolves both:
 *   - The class data block (`SRD_CLASSES[className].combatToggles`)
 *   - The subclass data block (`SRD_CLASSES[className].subclasses[subclass]
 *     .combatToggles`, via contentResolver.getSubclassData)
 *
 * Custom CW classes/subclasses can declare their own combatToggles the
 * same way and are picked up automatically.
 *
 * @param {object} playerStats
 * @param {string} flag - the toggle flag (e.g., 'sneakAttackActive',
 *   'attackingHexbladeCursedTarget')
 * @returns {boolean}
 */
export function classOwnsCombatToggleFlag(playerStats, flag) {
  if (!playerStats || !flag) return false;
  if (!Array.isArray(playerStats.classes)) return false;
  return playerStats.classes.some((c) => {
    if (!c || !c.className) return false;
    const classLevel = c.level || 0;

    // [PATH 1]: Class-level ownership.
    const classData = getClassData(c.className);
    if (matchesToggleEntry(classData?.combatToggles, flag, classLevel)) {
      return true;
    }

    // [PATH 2]: Subclass-level ownership. The subclass entry only resolves
    // when c.subclass is set — characters at sub-3 levels (or whatever
    // their class's subclassLevel is) won't match here yet.
    if (c.subclass) {
      const subclassData = getSubclassData(c.className, c.subclass);
      if (matchesToggleEntry(subclassData?.combatToggles, flag, classLevel)) {
        return true;
      }
    }

    return false;
  });
}
